import React, {Suspense, lazy, useEffect} from 'react';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {AuthProvider} from './security/AuthProvider';
import ProtectedRoute from './security/ProtectedRoute';
import {LoadingProvider} from './LoadingContext';
import {SettingsProvider} from './contexts/SettingsContext';
import Login from './pages/login/Login';
import Spinner from './ui/Spinner';
import Layout from './Layout';
import {registerBuiltInPlugins} from "./plugins";
import Unauthorized from "./pages/unauthorized/Unauthorized";
import Analytics from "./pages/reporting/Analytics";
import ForgotPassword from "./pages/login/ForgotPassword";
import ResetPassword from "./pages/login/ResetPassword";
import SetupAccount from "./pages/login/SetupAccount";
import CommissionManager from "./pages/custom/commissions/CommissionManager";
import Automations from "./pages/automation/Automations";
import useTenant from './hooks/useTenant';
import { BladeProvider } from './components/Blade';
import {OverlayProvider} from "@react-aria/overlays";
import ReportCreator from "./pages/reporting/ReportCreator";


// Use React.lazy for route-based code splitting
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Employees = lazy(() => import('./pages/employees/Employees'));
const EmployeeDetails = lazy(() => import('./pages/employees/EmployeeDetails'));
const Payrolls = lazy(() => import('./pages/payroll/PayrollPage'));
const Timetracking = lazy(() => import('./pages/timetracking/Timetracking'));
const CommissionTransactions = lazy(() => import('./pages/custom/commissions/Transactions'));
const CommissionPayoffs = lazy(() => import('./pages/custom/commissions/Payoffs'));
const CommissionPayoffDetails = lazy(() => import('./pages/custom/commissions/CommissionPayoffDetails'));
const Performance = lazy(() => import('./pages/custom/performance/Performance'));
const Sales = lazy(() => import('./pages/custom/sales/Sales'));
const MySales = lazy(() => import('./pages/custom/sales/MySales'));
const AgentSales = lazy(() => import('./pages/custom/sales/AgentSalesDetails'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const DisplaySettings = lazy(() => import('./pages/settings/display/DisplaySettings'));
const Absences = lazy(() => import('./pages/settings/workingtime_absence/Absences'));
const EmployeeRoles = lazy(() => import('./pages/settings/employees/EmployeeRoles'));
const WorkTimeModels = lazy(() => import('./pages/settings/workingtime_absence/WorkingTimeModels'));
const OrganizationalUnits = lazy(() => import('./pages/settings/organization/OrganizationalUnitsPage'));
const AbsenceCalendar = lazy(() => import('./pages/absence-calendar/AbsenceCalendar'));

// Document templates
const DocumentTemplates = lazy(() => import('./pages/document-templates/DocumentTemplates'));
const TemplateEditor = lazy(() => import('./pages/document-templates/TemplateEditor'));
const TemplateInstantiationView = lazy(() => import('./pages/document-templates/TemplateInstantiationView'));

// Create a loading fallback that matches your app's style
const PageLoadingFallback = () => <div style={{padding: '20px'}}><Spinner/></div>;

function App() {

    const {tenant, loading} = useTenant();

    useEffect(() => {
        registerBuiltInPlugins();
    }, []);

    useEffect(() => {
        if (tenant) {
            document.title = `${tenant?.companyName} - Portal`;
        }
    }, [tenant]);

    return (
        <BrowserRouter>
            {/*
            AuthProvider Configuration Options:
            - autoRedirectOnExpiration: true = immediate redirect, false = show modal (default: false)
            - tokenCheckInterval: milliseconds between token validity checks (default: 5 minutes)
            */}
            <AuthProvider autoRedirectOnExpiration={false} tokenCheckInterval={4 * 60 * 1000}>
                <SettingsProvider>
                    <LoadingProvider>
                        <OverlayProvider>
                            <BladeProvider>
                                <Routes>
                                    {/* Authentication routes outside the protected layout */}
                                    <Route path="/login" element={<Login/>}/>
                                    <Route path="/logout" element={<Login/>}/>
                                    <Route path="/forgot-password" element={<ForgotPassword/>}/>
                                    <Route path="/reset-password" element={<ResetPassword/>}/>
                                    <Route path="/setup-account" element={<SetupAccount/>}/>
                                    <Route path="/unauthorized" element={<Unauthorized/>}/>

                                    {/* Protected routes with persistent layout */}
                                    <Route path="/" element={<ProtectedRoute/>}>
                                        <Route element={<Layout/>}>
                                            {/* Default route */}
                                            <Route index element={
                                                <Suspense fallback={<PageLoadingFallback/>}>
                                                    <Dashboard/>
                                                </Suspense>
                                            }/>

                                            {/* Dynamic routes with lazy loading */}
                                            <Route path="dashboard" element={
                                                <Suspense fallback={<PageLoadingFallback/>}>
                                                    <Dashboard/>
                                                </Suspense>
                                            }/>

                                            <Route element={<ProtectedRoute roles={['employee.view']}/>}>
                                                <Route path="employees" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Employees/>
                                                    </Suspense>
                                                }/>

                                                <Route path="employees/:employeeCode" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <EmployeeDetails/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['absence-calendar.view']}/>}>
                                                <Route path="absence-calendar" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <AbsenceCalendar/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['timesheet.view']}/>}>
                                                <Route path="timetracking" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Timetracking/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['report.view']}/>}>
                                                <Route path="analytics" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Analytics/>
                                                    </Suspense>
                                                }/>

                                                <Route path="analytics/create-report" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <ReportCreator />
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['payroll.view']}/>}>
                                                <Route path="payrolls" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Payrolls/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['document-templates.view']}/>}>
                                                <Route path="document-templates" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <DocumentTemplates/>
                                                    </Suspense>
                                                }/>

                                                <Route path="document-templates/:templateId/edit" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <TemplateEditor/>
                                                    </Suspense>
                                                }/>

                                                <Route path="document-templates/:templateId/instantiate" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <TemplateInstantiationView/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['automation.view']}/>}>
                                                <Route path="automations" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Automations/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['settings.view']}/>}>
                                                <Route path="settings" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Settings/>
                                                    </Suspense>
                                                }/>

                                                <Route path="settings/employees/roles" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <EmployeeRoles/>
                                                    </Suspense>
                                                }/>

                                                <Route path="settings/absence-types" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Absences/>
                                                    </Suspense>
                                                }/>

                                                <Route path="settings/work-time-models" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <WorkTimeModels/>
                                                    </Suspense>
                                                }/>

                                                <Route path="settings/organizational-units" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <OrganizationalUnits/>
                                                    </Suspense>
                                                }/>

                                                <Route path="settings/display" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <DisplaySettings/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route element={<ProtectedRoute roles={['fourlook.commission.view']}/>}>
                                                <Route path="custom/commissions/management" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <CommissionManager/>
                                                    </Suspense>
                                                }/>

                                                <Route path="custom/commissions/transactions" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <CommissionTransactions/>
                                                    </Suspense>
                                                }/>

                                                <Route path="custom/commissions/payoffs" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <CommissionPayoffs/>
                                                    </Suspense>
                                                }/>

                                                <Route path="custom/commissions/payoffs/:year/:month/:employeeId"
                                                       element={
                                                           <Suspense fallback={<PageLoadingFallback/>}>
                                                               <CommissionPayoffDetails/>
                                                           </Suspense>
                                                       }/>
                                            </Route>

                                            <Route path="custom/performance" element={
                                                <Suspense fallback={<PageLoadingFallback/>}>
                                                    <Performance/>
                                                </Suspense>
                                            }/>

                                            <Route element={<ProtectedRoute roles={['fourlook.sales.view']}/>}>
                                                <Route path="custom/sales/overview" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <Sales/>
                                                    </Suspense>
                                                }/>

                                                <Route path="custom/sales/:employeeId" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <AgentSales/>
                                                    </Suspense>
                                                }/>

                                                <Route path="custom/sales/mysales" element={
                                                    <Suspense fallback={<PageLoadingFallback/>}>
                                                        <MySales/>
                                                    </Suspense>
                                                }/>
                                            </Route>

                                            <Route path="*" element={<div>Not Found</div>}/>
                                        </Route>
                                    </Route>
                                </Routes>
                            </BladeProvider>
                        </OverlayProvider>
                    </LoadingProvider>
                </SettingsProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;